/**
 * Maps a Firebase Auth error (or anything thrown by the auth thunks) to a translation key
 * under `other.errorMessages.auth.*`, so the user sees what actually went wrong instead of a
 * generic "wrong email or password".
 */
export type AuthErrorKey =
  | 'invalidCredentials'
  | 'emailInUse'
  | 'invalidEmail'
  | 'weakPassword'
  | 'tooManyRequests'
  | 'userDisabled'
  | 'network'
  | 'popupClosed'
  | 'profileNotSaved'
  | 'unknown';

const BY_CODE: Record<string, AuthErrorKey> = {
  'auth/wrong-password': 'invalidCredentials',
  'auth/user-not-found': 'invalidCredentials',
  'auth/invalid-credential': 'invalidCredentials',
  'auth/invalid-login-credentials': 'invalidCredentials',
  'auth/email-already-in-use': 'emailInUse',
  'auth/account-exists-with-different-credential': 'emailInUse',
  'auth/invalid-email': 'invalidEmail',
  'auth/missing-email': 'invalidEmail',
  'auth/weak-password': 'weakPassword',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/user-disabled': 'userDisabled',
  'auth/network-request-failed': 'network',
  'auth/popup-closed-by-user': 'popupClosed',
  'auth/cancelled-popup-request': 'popupClosed',
  'auth/popup-blocked': 'popupClosed',
};

/**
 * Server codes that firebase 9.x does not know yet: it reports them as `auth/internal-error`
 * with the raw server code in the message (e.g. INVALID_LOGIN_CREDENTIALS, returned for a wrong
 * password or unknown email when email enumeration protection is on).
 */
const BY_SERVER_MESSAGE: [string, AuthErrorKey][] = [
  ['INVALID_LOGIN_CREDENTIALS', 'invalidCredentials'],
  ['INVALID_PASSWORD', 'invalidCredentials'],
  ['EMAIL_NOT_FOUND', 'invalidCredentials'],
  ['EMAIL_EXISTS', 'emailInUse'],
  ['TOO_MANY_ATTEMPTS_TRY_LATER', 'tooManyRequests'],
  ['USER_DISABLED', 'userDisabled'],
];

export function getAuthErrorKey(err: unknown): AuthErrorKey {
  const code = typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code?: unknown }).code)
    : '';
  if (code && BY_CODE[code]) return BY_CODE[code];
  const message = err instanceof Error ? err.message : '';
  const fromMessage = BY_SERVER_MESSAGE.find(([serverCode]) => message.includes(serverCode));
  if (fromMessage) return fromMessage[1];
  if (err instanceof TypeError) return 'network'; // fetch() failed
  return 'unknown';
}

export const authErrorI18nKey = (key: AuthErrorKey) => `other.errorMessages.auth.${key}`;
